/** Shared form model for the Create/Edit User page and its Political Person layout. */

export interface AddressValues {
  line: string;
  city: string;
  state: string;
  pin: string;
  contact: string;
}
export const emptyAddress = (): AddressValues => ({ line: '', city: '', state: '', pin: '', contact: '' });

export interface PaValues {
  name: string;
  phone: string;
  email: string;
  designation: string;
}
export const emptyPa = (): PaValues => ({ name: '', phone: '', email: '', designation: '' });

export interface UserFormValues {
  directory: string;
  organization: number | '';
  /** Composed name used by the non-elected directories (single input). */
  fullName: string;
  email: string;
  /** One or more contact numbers. */
  contact: string[];
  /** Selected location key — EC area code (cascade) or org-node key "n:15" (party). */
  location: string;
  /** Selected role id — organization directory. */
  role: number | '';
  /** Designation / Department tags — non-elected directories. */
  designation: string[];
  department: string[];
  active: boolean;

  /* ---- Political Person (elected) — richer gazette form ---- */
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  landline: string;
  landlineExt: string;
  whatsapp: string;
  /** Which mobile number mirrors into WhatsApp: a `contact` index, or -1 for a manual entry. */
  whatsappIndex: number;
  politicalParty: string;
  electionResult: string;
  /** Primary designation (Select) + extra ministerial roles (tags) → merged on submit. */
  designationValue: string;
  ministerialRoles: string[];
  dob: string;
  gender: string;
  religion: string;
  caste: string;
  reservation: string;
  addresses: {
    constituencyHome: AddressValues;
    constituencyOffice: AddressValues;
    capitalHome: AddressValues;
    capitalOffice: AddressValues;
  };
  pa: { primary: PaValues; secondary: PaValues };
  bioSummary: string;
  politicalHistory: string;
  /** UI-only review flags (not persisted). */
  markIncomplete: boolean;

  /* ---- Government Official (bureaucracy) extras (UI-only) ---- */
  officeAddress: string;
  /** Free-text additional roles entered as tags (descriptive, not functional); sent in `profile`. */
  additionalRoles: string[];

  /* ---- Client Team User (organization) extras (UI-only) ---- */
  roleClassification: string;
  boundaryAuthority: string;
  boundaryLevel: string;
  /** Multi-role manager — each entry is a role + scope + optional location (UI-only). */
  assignedRoles: AssignedRole[];
}

/** A single assigned role in the Client Team "Assigned Roles & Scopes" manager. */
export type RoleScope = 'functional' | 'territorial' | 'hybrid';
export interface AssignedRole {
  id: string; // stable client key (index-based, e.g. `r3`)
  roleId: number | '';
  roleName: string; // label snapshot, for card display
  scopeType: RoleScope;
  department: string; // functional/hybrid only
  designation: string; // functional/hybrid only
  boundaryAuthority: string; // eci | sec | po — territorial/hybrid only
  boundaryLevel: string; // electoral boundary level — territorial/hybrid only
  location: string; // EC area code for territorial/hybrid; '' for functional (global)
  locationLabel: string; // breadcrumb shown on the card + edit prefill
}

/** Scope-type metadata — badge/segmented-control label + colours + icon key. */
export const ROLE_SCOPES: {
  key: RoleScope;
  label: string;
  badge: string; // badge bg + text classes
  iconWrap: string; // card icon square bg + text classes
}[] = [
  {
    key: 'functional',
    label: 'Functional',
    badge: 'bg-[#f3e8ff] text-[#7e22ce]',
    iconWrap: 'bg-[#f3e8ff] text-[#7e22ce]',
  },
  {
    key: 'territorial',
    label: 'Territorial',
    badge: 'bg-[#dbeafe] text-[#1d4ed8]',
    iconWrap: 'bg-[#dbeafe] text-[#1d4ed8]',
  },
  {
    key: 'hybrid',
    label: 'Hybrid',
    badge: 'bg-[#dcfce7] text-[#15803d]',
    iconWrap: 'bg-[#dcfce7] text-[#15803d]',
  },
];

/* Static option lists for the UI-only demographic / affiliation selects. */
export const TITLES = [
  // Common courtesy titles
  'Mr.',
  'Ms.',
  'Mrs.',
  'Miss',
  'Mx.',
  'Master',
  // Professional & academic titles
  'Dr.',
  'Prof.',
  'Rev.',
  'Hon.',
  // Indian courtesy titles
  'Shri',
  'Smt.',
  'Kumari',
];
export const GENDERS = ['Male', 'Female', 'Other'];
export const RELIGIONS = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jain', 'Parsi', 'Other'];
export const RESERVATIONS = ['General', 'SC', 'ST', 'OBC', 'EWS'];
export const ELECTION_RESULTS = [
  'Winner (Incumbent / Elected)',
  'Runner-up',
  'Contested',
  'Not Contested',
];
export const PARTIES = ['BJP', 'INC', 'AAP', 'DMK', 'AIADMK', 'TMC', 'BSP', 'SP', 'NCP', 'Other'];

/* Client Team User (organization) classifications, boundary authorities + EC levels. */
export const ROLE_CLASSIFICATIONS = [
  { key: 'territorial', label: 'Territorial Role' },
  { key: 'functional', label: 'Functional Role' },
  { key: 'hybrid', label: 'Hybrid Role' },
];
export const BOUNDARY_AUTHORITIES = [
  { key: 'eci', label: 'ECI (Election Commission of India)' },
  { key: 'sec', label: 'SEC (State Election Commission)' },
  { key: 'po', label: 'PO (Political Organization)' },
];
export const BOUNDARY_LEVELS = [
  'State',
  'Parliament (MP)',
  'District',
  'Assembly Constituency (AC)',
  'Block / ULB',
  'Gram Panchayat',
  'Ward',
  'Booth',
];

/**
 * Master list of designations for the Designation dropdown. TEMPORARY starter set —
 * Poonam's team will supply the authoritative list (ideally via an API) later.
 */
export const DESIGNATIONS = [
  'Member of Legislative Assembly (MLA)',
  'Member of Parliament (MP)',
  'Member of Legislative Council (MLC)',
  'Chief Minister',
  'Deputy Chief Minister',
  'Cabinet Minister',
  'Minister of State',
  'Speaker',
  'Deputy Speaker',
  'Mayor',
  'Deputy Mayor',
  'Corporator / Councillor',
  'Zilla Parishad President',
  'Sarpanch',
  'Party President',
  'General Secretary',
  'Chairman',
  'Spokesperson',
];

/** Indian states + union territories, for the address State dropdown. */
export const STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
];

/** Master list of departments for the Department dropdown. TEMPORARY sample set. */
export const DEPARTMENTS = [
  'Revenue',
  'Home',
  'Finance',
  'Health & Family Welfare',
  'Education',
  'Public Works Department (PWD)',
  'Urban Development',
  'Rural Development',
  'Agriculture',
  'Water Resources',
  'Transport',
  'Social Welfare',
  'Police',
  'Forest & Environment',
  'Information & Public Relations',
];

export const toSelectOptions = (arr: string[]) => arr.map((v) => ({ value: v, label: v }));
